export const ESTADOS_BRASIL = [
  { sigla: "AC", nome: "Acre" },
  { sigla: "AL", nome: "Alagoas" },
  { sigla: "AP", nome: "Amapá" },
  { sigla: "AM", nome: "Amazonas" },
  { sigla: "BA", nome: "Bahia" },
  { sigla: "CE", nome: "Ceará" },
  { sigla: "DF", nome: "Distrito Federal" },
  { sigla: "ES", nome: "Espírito Santo" },
  { sigla: "GO", nome: "Goiás" },
  { sigla: "MA", nome: "Maranhão" },
  { sigla: "MT", nome: "Mato Grosso" },
  { sigla: "MS", nome: "Mato Grosso do Sul" },
  { sigla: "MG", nome: "Minas Gerais" },
  { sigla: "PA", nome: "Pará" },
  { sigla: "PB", nome: "Paraíba" },
  { sigla: "PR", nome: "Paraná" },
  { sigla: "PE", nome: "Pernambuco" },
  { sigla: "PI", nome: "Piauí" },
  { sigla: "RJ", nome: "Rio de Janeiro" },
  { sigla: "RN", nome: "Rio Grande do Norte" },
  { sigla: "RS", nome: "Rio Grande do Sul" },
  { sigla: "RO", nome: "Rondônia" },
  { sigla: "RR", nome: "Roraima" },
  { sigla: "SC", nome: "Santa Catarina" },
  { sigla: "SP", nome: "São Paulo" },
  { sigla: "SE", nome: "Sergipe" },
  { sigla: "TO", nome: "Tocantins" },
] as const;

export type EstadoBrasil = (typeof ESTADOS_BRASIL)[number];
export type SiglaEstadoBrasil = EstadoBrasil["sigla"];
export type MunicipioIbge = { id: number; nome: string };
export type LocalidadeUsuario = {
  estado: SiglaEstadoBrasil;
  cidadeId: number;
  cidade: string;
};

type FetchLocalidades = (input: string, init?: RequestInit & { next?: { revalidate: number } }) => Promise<Response>;

export function ehEstadoBrasil(valor: unknown): valor is SiglaEstadoBrasil {
  return typeof valor === "string" && ESTADOS_BRASIL.some((estado) => estado.sigla === valor);
}

export function podeConfigurarLocalidade(produto: string | null | undefined, papel: string | null | undefined, organizacaoAtiva = true) {
  return produto === "FOLLOW_ZAP" && podeUsarLocalidade(produto, papel, organizacaoAtiva);
}

export function podeUsarLocalidade(produto: string | null | undefined, papel: string | null | undefined, organizacaoAtiva = true) {
  return organizacaoAtiva && (produto === "FOLLOW_ZAP" || produto === "FOLLOW_PRO") && (papel === "USUARIO" || papel === "GESTOR");
}

export function lerLocalidadeSalva(valor: string | null | undefined): LocalidadeUsuario | null {
  if (!valor) return null;
  try {
    const localidade = JSON.parse(valor) as Partial<LocalidadeUsuario>;
    if (!ehEstadoBrasil(localidade.estado)) return null;
    if (!Number.isInteger(localidade.cidadeId) || Number(localidade.cidadeId) <= 0) return null;
    if (typeof localidade.cidade !== "string" || !localidade.cidade.trim()) return null;
    return {
      estado: localidade.estado,
      cidadeId: Number(localidade.cidadeId),
      cidade: localidade.cidade.trim(),
    };
  } catch {
    return null;
  }
}

export async function listarMunicipiosIbge(estado: SiglaEstadoBrasil, fetcher: FetchLocalidades = fetch) {
  if (!ehEstadoBrasil(estado)) throw new Error("Estado inválido.");

  const resposta = await fetcher(
    `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${estado}/municipios?orderBy=nome`,
    { signal: AbortSignal.timeout(5000), next: { revalidate: 86400 } },
  );
  if (!resposta.ok) throw new Error("Catálogo de municípios indisponível.");

  const dados = await resposta.json();
  if (!Array.isArray(dados)) throw new Error("Resposta inválida do catálogo de municípios.");

  const municipios = dados
    .filter((item): item is MunicipioIbge => Number.isInteger(item?.id) && item.id > 0 && typeof item?.nome === "string" && Boolean(item.nome.trim()))
    .map((item) => ({ id: item.id, nome: item.nome.trim() }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  if (municipios.length === 0) throw new Error("Resposta vazia do catálogo de municípios.");
  return municipios;
}

export async function validarCidadeIbge(estado: SiglaEstadoBrasil, cidadeId: number, fetcher?: FetchLocalidades) {
  if (!Number.isInteger(cidadeId) || cidadeId <= 0) return null;
  const municipios = await listarMunicipiosIbge(estado, fetcher);
  return municipios.find((municipio) => municipio.id === cidadeId) ?? null;
}
